/**
 * Keep returning visitors on the deployed build.
 *
 * vite.config's PWA is `registerType: 'autoUpdate'` (skipWaiting +
 * clientsClaim), but the generated /registerSW.js only registers the worker,
 * it never reloads. So the first visit after a deploy is answered from the OLD
 * worker's precache (old index.html + old JS); the new worker installs a few
 * seconds later and takes over a page that keeps running the old build until
 * the next reload. (2026-10-16: a customer still saw the Saved Foods row
 * without the tag button hours after it shipped.)
 *
 * - When the new worker takes control before the user has touched the page,
 *   reload right away onto the new build: nothing is lost yet.
 * - Once they are interacting we never reload under them (the diary's selected
 *   date and open dialogs live in memory); the new build loads next visit.
 * - A lazy screen whose file a deploy removed reloads onto the new build
 *   instead of failing (Vite's documented `vite:preloadError` hook).
 *
 * Every reload is guarded once per 30 s in sessionStorage, and skipped when
 * storage is unavailable, so a broken deploy can never loop.
 */

const RELOAD_GUARD_KEY = 'lv_build_reload_at'
const RELOAD_GUARD_MS = 30_000

function reloadOnce(): void {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0)
    if (Date.now() - last < RELOAD_GUARD_MS) return
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    return
  }
  window.location.reload()
}

export function keepBuildFresh(): void {
  if (typeof window === 'undefined') return

  window.addEventListener('vite:preloadError', () => reloadOnce())

  if (!('serviceWorker' in navigator)) return
  // No controller = first install (or a hard reload): the page is already the
  // current build, and clientsClaim's controllerchange must not reload it.
  const hadController = Boolean(navigator.serviceWorker.controller)
  let touched = false
  const markTouched = () => {
    touched = true
  }
  window.addEventListener('pointerdown', markTouched, { capture: true, once: true, passive: true })
  window.addEventListener('keydown', markTouched, { capture: true, once: true })

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController && !touched) reloadOnce()
  })
}
